import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useState, MouseEvent } from "react";
import { MenuBorderColor } from "shared/constants";
import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { CustomTooltip } from "@web-applications/allinga-web-components";
import { sampleRateOptionsAtom } from "atoms";

interface SampleRateSelectorProps {
  showBorder?: boolean;
  paddingRight?: string;
  onSampleRateChange?: (sampleRate: string) => void;
  onClick?: (event: MouseEvent) => void;
}

export default function SampleRateSelector({
  showBorder = false,
  paddingRight = "0",
  onSampleRateChange,
  onClick,
}: SampleRateSelectorProps): JSX.Element {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const sampleRateOptions = useAtomValue(sampleRateOptionsAtom);
  const [selectedSampleRate, setSelectedSampleRate] = useState<string>(
    sampleRateOptions[0]?.value
  );

  const handleTooltipClose = () => setTooltipOpen(false);

  const handleTooltipOpen = () => {
    if (!menuOpen) setTooltipOpen(true);
  };

  const handleSampleRateChange = (newSampleRate: string) => {
    setSelectedSampleRate(newSampleRate);
    if (onSampleRateChange) {
      onSampleRateChange(newSampleRate);
    }
    handleTooltipClose();
  };

  const handleClick = (event: MouseEvent) => {
    if (onClick) {
      onClick(event);
    }
  };

  const { t } = useTranslation();

  return (
    <Stack gap={2} onClick={handleClick}>
      {sampleRateOptions.length > 0 && (
        <CustomTooltip
          title={t("components.sampleRate.tooltip")}
          open={tooltipOpen && !menuOpen}
          onOpen={handleTooltipOpen}
          onClose={handleTooltipClose}
          component="span"
          forceShow
        >
          <FormControl
            fullWidth
            sx={{
              ...(showBorder
                ? {}
                : {
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderRadius: 0,
                        border: "none",
                      },
                    },
                  }),
            }}
            id="sample-rate-selector"
          >
            <Select
              value={selectedSampleRate}
              disabled={sampleRateOptions.length === 1}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 275,
                    border: `1px solid ${MenuBorderColor}`,
                  },
                },
              }}
              onOpen={() => setMenuOpen(true)}
              onClose={() => setMenuOpen(false)}
              onChange={(event) => handleSampleRateChange(event.target.value)}
              onClick={(event) => {
                event.stopPropagation();
              }}
              sx={{
                height: "40px",
                "& .MuiSelect-select": {
                  padding: "0px 12px",
                },
                "&:hover": {
                  background: "linear-gradient(120deg, #ebf5f3, #eaf9fa)",
                },
              }}
            >
              {sampleRateOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Typography
                    variant="body1"
                    sx={{ paddingRight: paddingRight }}
                  >
                    {option.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CustomTooltip>
      )}
    </Stack>
  );
}
